<template>
  <section class="leaderboard-card" :aria-label="`${title} leaderboard`">
    <header class="leaderboard-card__header">
      <span class="leaderboard-card__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24">
          <path d="M8 4h8v3.5c0 3.2-1.4 5.5-4 6.5-2.6-1-4-3.3-4-6.5V4Z" />
          <path d="M8 6H5v1.5c0 2 1.4 3.5 3.6 3.7M16 6h3v1.5c0 2-1.4 3.5-3.6 3.7M12 14v3M8.5 20h7M10 17h4" />
        </svg>
      </span>
      <div class="leaderboard-card__heading">
        <span class="leaderboard-card__eyebrow">Standings</span>
        <h2>{{ title }}</h2>
      </div>
      <span v-if="!loading && data.length" class="leaderboard-card__count">
        Top {{ data.length }}
      </span>
    </header>

    <div class="leaderboard-card__columns" aria-hidden="true">
      <span>#</span>
      <span>Sister</span>
      <span>{{ subTitle }}</span>
    </div>

    <div v-if="loading" class="leaderboard-card__skeleton" role="status" aria-label="Loading leaderboard">
      <div v-for="row in 10" :key="`leaderboard-loading-${row}`" class="leaderboard-card__skeleton-row">
        <span></span>
        <span></span>
        <span></span>
      </div>
    </div>

    <ol v-else-if="data.length" class="leaderboard-card__list">
      <li
        v-for="(crowd, index) in data"
        :key="`${subTitle}-${crowd.hashid}-${crowd.score}-${index}`"
        class="leaderboard-card__row"
      >
        <span
          class="leaderboard-card__rank"
          :class="rankClass(index)"
          :aria-label="`Rank ${index + 1}`"
        >
          {{ index + 1 }}
        </span>
        <router-link
          class="leaderboard-card__name"
          :title="crowd.name"
          :to="{ name: 'SororityWarsProfileHome', params: { playerId: crowd.hashid } }"
        >
          <string-viewer-x :message="crowd.name" />
        </router-link>
        <strong class="leaderboard-card__score">
          <span v-if="currency">$</span>{{ $number.withCommas(crowd.score) }}
        </strong>
      </li>
    </ol>

    <div v-else class="leaderboard-card__empty">
      <span class="leaderboard-card__empty-icon" aria-hidden="true">—</span>
      <strong>No standings yet</strong>
      <span>Check back after players make their moves.</span>
    </div>
  </section>
</template>

<script>
export default {
  name: 'InCrowdDisplay',
  props: {
    title: {
      type: String,
      required: true
    },
    subTitle: {
      type: String,
      required: true
    },
    data: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    currency: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    rankClass (index) {
      if (index === 0) return 'leaderboard-card__rank--first'
      if (index === 1) return 'leaderboard-card__rank--second'
      if (index === 2) return 'leaderboard-card__rank--third'
      return ''
    }
  }
}
</script>

<style scoped>
.leaderboard-card {
  overflow: hidden;
  min-width: 0;
  background: #fff;
  border: 1px solid #238cbd;
  border-radius: 8px;
}

.leaderboard-card__header {
  display: flex;
  min-height: 58px;
  align-items: center;
  gap: 8px;
  padding: 7px 8px;
  color: #0b5aa8;
  background: #d1ebf1 url("//assets.sororitywars.app/backgrounds/blue-background.jpg") repeat center top;
  border-bottom: 1px solid rgba(35, 140, 189, 0.45);
}

.leaderboard-card__icon {
  display: flex;
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  align-items: center;
  justify-content: center;
  color: #ad1f51;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(232, 64, 99, 0.38);
  border-radius: 7px;
}

.leaderboard-card__icon svg {
  width: 20px;
  height: 20px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.7;
}

.leaderboard-card__heading {
  overflow: hidden;
  min-width: 0;
  flex: 1 1 auto;
}

.leaderboard-card__eyebrow {
  display: block;
  color: #5b7183;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  line-height: 1.2;
  text-transform: uppercase;
}

.leaderboard-card__heading h2 {
  overflow: hidden;
  margin: 1px 0 0;
  color: #0b5aa8;
  font-size: 15px;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.leaderboard-card__count {
  flex: 0 0 auto;
  padding: 3px 6px;
  color: #ad1f51;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(232, 64, 99, 0.32);
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 800;
}

.leaderboard-card__columns,
.leaderboard-card__row,
.leaderboard-card__skeleton-row {
  display: grid;
  grid-template-columns: 30px minmax(0, 1fr) minmax(90px, auto);
  align-items: center;
  gap: 6px;
}

.leaderboard-card__columns {
  min-height: 32px;
  padding: 0 8px;
  color: #fff;
  background: #145c89;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.leaderboard-card__columns span:last-child {
  text-align: right;
}

.leaderboard-card__list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.leaderboard-card__row {
  min-height: 39px;
  padding: 4px 8px;
  border-bottom: 1px solid rgba(35, 140, 189, 0.14);
  transition: background-color 160ms ease, box-shadow 160ms ease;
}

.leaderboard-card__row:nth-child(even) {
  background: #f4fafc;
}

.leaderboard-card__row:last-child {
  border-bottom: 0;
}

.leaderboard-card__row:hover {
  background: #edf8fb;
  box-shadow: inset 3px 0 0 #e84063;
}

.leaderboard-card__rank {
  display: inline-flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  color: #536a7b;
  background: #edf3f6;
  border: 1px solid #b9cbd5;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 800;
}

.leaderboard-card__rank--first {
  color: #795600;
  background: #ffe99a;
  border-color: #d5a919;
}

.leaderboard-card__rank--second {
  color: #4e5e6a;
  background: #e4ebef;
  border-color: #a4b3bd;
}

.leaderboard-card__rank--third {
  color: #79451f;
  background: #efc39e;
  border-color: #c47b43;
}

.leaderboard-card__name {
  overflow: hidden;
  min-width: 0;
  color: #145c89 !important;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.leaderboard-card__name:hover {
  color: #ad1f51 !important;
  text-decoration: underline;
}

.leaderboard-card__score {
  overflow: hidden;
  min-width: 0;
  color: #173f5f;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  text-align: right;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.leaderboard-card__empty {
  display: flex;
  min-height: 160px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 10px;
  color: #5b7183;
  font-size: 13px;
  text-align: center;
}

.leaderboard-card__empty-icon {
  display: flex;
  width: 30px;
  height: 30px;
  align-items: center;
  justify-content: center;
  margin-bottom: 2px;
  color: #ad1f51;
  background: #fff1f5;
  border: 1px solid rgba(232, 64, 99, 0.35);
  border-radius: 7px;
  font-size: 17px;
}

.leaderboard-card__empty strong {
  color: #ad1f51;
  font-size: 14px;
}

.leaderboard-card__skeleton {
  padding: 4px 8px;
}

.leaderboard-card__skeleton-row {
  min-height: 39px;
}

.leaderboard-card__skeleton-row span {
  height: 14px;
  background: linear-gradient(90deg, #e6f1f5 25%, #f7fbfc 50%, #e6f1f5 75%);
  background-size: 200% 100%;
  border-radius: 4px;
  animation: crowd-shimmer 1.2s ease-in-out infinite;
}

.leaderboard-card__skeleton-row span:first-child {
  width: 24px;
  height: 24px;
  border-radius: 6px;
}

.leaderboard-card__skeleton-row span:last-child {
  width: 70px;
  justify-self: end;
}

@keyframes crowd-shimmer {
  from {
    background-position: 200% 0;
  }

  to {
    background-position: -200% 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .leaderboard-card__skeleton-row span {
    animation: none;
  }
}

@media only screen and (max-width: 420px) {
  .leaderboard-card__columns,
  .leaderboard-card__row,
  .leaderboard-card__skeleton-row {
    grid-template-columns: 28px minmax(0, 1fr) minmax(78px, auto);
    gap: 5px;
  }

  .leaderboard-card__columns,
  .leaderboard-card__row {
    padding-right: 6px;
    padding-left: 6px;
  }

  .leaderboard-card__name {
    font-size: 13px;
  }

  .leaderboard-card__score {
    font-size: 12px;
  }
}
</style>
